/**
 * Минимальный рантайм для перенесённой оболочки прототипа (повторяет семантику support.js из claude.ai/design):
 * DCLogic — базовый класс контроллера, DCHost — React-обёртка, хелперы для сгенерированного template.tsx.
 */
import React, { type ReactNode } from 'react';

type State = Record<string, any>;

export class DCLogic<P extends State = State> {
  props: P;
  state: State = {};
  __host?: DCHost;
  constructor(props: P) {
    this.props = props || ({} as P);
  }
  setState(update: State | ((s: State) => State | null), cb?: () => void) {
    this.__host?.__setLogicState(update, cb);
  }
  forceUpdate() {
    this.__host?.forceUpdate();
  }
  componentDidMount() {}
  componentDidUpdate(_prevProps: P) {}
  componentWillUnmount() {}
  /** Плоский объект, по которому рендерится шаблон (поверх props). */
  renderVals(): State {
    return {};
  }
}

interface HostProps {
  logic: new (props: any) => DCLogic;
  template: (v: any) => ReactNode;
  props: State;
}

/** Хост: состояние контроллера применяется сразу (как в рантайме прототипа), рендер — Template({...props, ...renderVals()}). */
export class DCHost extends React.Component<HostProps, { v: number; err: string | null }> {
  logic: DCLogic;
  override state = { v: 0, err: null as string | null };

  constructor(p: HostProps) {
    super(p);
    this.logic = new p.logic(p.props);
    this.logic.__host = this;
  }

  static getDerivedStateFromError(e: unknown) {
    return { err: e instanceof Error ? e.message : String(e) };
  }

  override componentDidCatch(e: unknown, info: React.ErrorInfo) {
    console.error('[legacy] render error', e, info.componentStack);
  }

  __setLogicState(update: State | ((s: State) => State | null), cb?: () => void) {
    const prev = this.logic.state;
    const patch = typeof update === 'function' ? update(prev) : update;
    if (patch == null) return cb?.();
    this.logic.state = { ...prev, ...patch };
    this.setState((s) => ({ v: s.v + 1 }), cb);
  }

  override componentDidMount() {
    this.logic.componentDidMount();
  }

  /** Как в рантайме прототипа: после КАЖДОГО обновления (контроллер перерисовывает в нём острова). */
  override componentDidUpdate(prev: HostProps) {
    this.logic.props = this.props.props;
    try {
      this.logic.componentDidUpdate(prev.props);
    } catch (e) {
      console.error('[legacy] componentDidUpdate', e);
    }
  }

  override componentWillUnmount() {
    this.logic.componentWillUnmount();
  }

  override render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 24, fontFamily: 'system-ui', color: '#b42318' }}>
          Ошибка интерфейса: {this.state.err}. Обновите страницу.
        </div>
      );
    }
    return this.props.template({ ...this.props.props, ...this.logic.renderVals() });
  }
}

// ---------- хелперы для template.tsx ----------

const kebabToCamel = (s: string) => s.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());

/** CSS-строка → объект стилей (как cssToObj рантайма: деление по «;» вне скобок и кавычек). */
export function css(text: string): React.CSSProperties {
  const o: Record<string, string> = {};
  let depth = 0;
  let quote = '';
  let start = 0;
  const push = (decl: string) => {
    const i = decl.indexOf(':');
    if (i < 0) return;
    const prop = decl.slice(0, i).trim();
    o[prop.startsWith('--') ? prop : kebabToCamel(prop)] = decl.slice(i + 1).trim();
  };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quote) {
      if (c === quote) quote = '';
    } else if (c === '"' || c === "'") quote = c;
    else if (c === '(') depth++;
    else if (c === ')') depth = Math.max(0, depth - 1);
    else if (c === ';' && depth === 0) {
      push(text.slice(start, i));
      start = i + 1;
    }
  }
  push(text.slice(start));
  return o as React.CSSProperties;
}

/** Текстовая подстановка: null/undefined/boolean не рендерятся, элементы и массивы — как есть. */
export function txt(v: unknown): ReactNode {
  if (v === null || v === undefined || typeof v === 'boolean') return null;
  if (React.isValidElement(v) || Array.isArray(v)) return v as ReactNode;
  return String(v);
}

export const arr = (v: unknown): any[] => (Array.isArray(v) ? v : []);
export const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
